import { useEffect, useState } from "react";
import Topbar from "../../components/Topbar";
import api from "../../lib/api";
import { toast } from "sonner";
import { ToggleLeft, ToggleRight, Loader2, ShieldAlert } from "lucide-react";

export default function SystemFlags() {
  const [flags, setFlags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingKey, setSavingKey] = useState(null);
  const [drafts, setDrafts] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/system-flags");
      setFlags(r.data.flags || []);
      const d = {};
      (r.data.flags || []).forEach(f => {
        if (f.type === "int") d[f.key] = f.value;
        if (f.type === "email_list") d[f.key] = (f.value || []).join(", ");
      });
      setDrafts(d);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudieron cargar los flags");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const toggleBool = async (flag) => {
    setSavingKey(flag.key);
    try {
      const r = await api.put(`/admin/system-flags/${flag.key}`, { value: !flag.value });
      setFlags(prev => prev.map(f => f.key === flag.key ? { ...f, value: r.data.value, updated_at: r.data.updated_at } : f));
      toast.success(`"${flag.label}" actualizado a ${r.data.value ? "activado" : "desactivado"}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al guardar el flag");
    } finally { setSavingKey(null); }
  };

  const saveInt = async (flag) => {
    const val = parseInt(drafts[flag.key], 10);
    if (Number.isNaN(val)) { toast.error("Ingresa un número válido"); return; }
    setSavingKey(flag.key);
    try {
      const r = await api.put(`/admin/system-flags/${flag.key}`, { value: val });
      setFlags(prev => prev.map(f => f.key === flag.key ? { ...f, value: r.data.value, updated_at: r.data.updated_at } : f));
      toast.success(`"${flag.label}" actualizado a ${r.data.value}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al guardar el flag");
    } finally { setSavingKey(null); }
  };

  const saveEmailList = async (flag) => {
    const emails = (drafts[flag.key] ?? "")
      .split(/[,;\n]/)
      .map(e => e.trim())
      .filter(Boolean);
    setSavingKey(flag.key);
    try {
      const r = await api.put(`/admin/system-flags/${flag.key}`, { value: emails });
      setFlags(prev => prev.map(f => f.key === flag.key ? { ...f, value: r.data.value, updated_at: r.data.updated_at } : f));
      setDrafts(prev => ({ ...prev, [flag.key]: (r.data.value || []).join(", ") }));
      toast.success(r.data.value.length
        ? `"${flag.label}": ${r.data.value.length} correo(s) guardado(s)`
        : `"${flag.label}" quedó vacío — el aviso vuelve a ir a los aprobadores de la regla`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al guardar los correos");
    } finally { setSavingKey(null); }
  };

  return (
    <>
      <Topbar title="Feature flags del sistema" subtitle="Interruptores globales sin necesidad de deploy" />
      <div className="p-6 pb-28 space-y-5 max-w-3xl" data-testid="system-flags-page">
        <div className="flex items-start gap-3 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900" data-testid="system-flags-warning-banner">
          <ShieldAlert className="w-5 h-5 shrink-0 mt-0.5" />
          <span>Estos flags controlan comportamiento crítico (2FA, correos, módulos). Los correos de alertas (conciliación, flujo de caja) están apagados por defecto — la alerta in-app se crea siempre, sin importar este flag.</span>
        </div>

        {loading ? (
          <div className="space-y-3">{[1, 2, 3].map(i => <div key={i} className="bg-white rounded-xl border border-slate-200 h-24 animate-pulse" />)}</div>
        ) : (
          <div className="space-y-3" data-testid="system-flags-list">
            {flags.map(flag => (
              <div key={flag.key} className={`bg-white rounded-xl border border-slate-200 p-4 gap-4 ${flag.type === "email_list" ? "space-y-3" : "flex items-start justify-between"}`} data-testid={`system-flag-row-${flag.key}`}>
                <div className="flex-1">
                  <div className="font-semibold text-slate-900 text-sm">{flag.label}</div>
                  <div className="text-xs text-slate-500 mt-1">{flag.description}</div>
                  {flag.updated_at && (
                    <div className="text-[11px] text-slate-400 mt-1">Última actualización: {new Date(flag.updated_at).toLocaleString("es-MX")}</div>
                  )}
                </div>

                {flag.type === "email_list" ? (
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      placeholder="compras@chizychiz.com, gustavo@chizychiz.com"
                      value={drafts[flag.key] ?? ""}
                      onChange={e => setDrafts(prev => ({ ...prev, [flag.key]: e.target.value }))}
                      className="flex-1 border border-slate-200 rounded-lg px-3 py-2 text-sm"
                      data-testid={`system-flag-emails-${flag.key}`}
                    />
                    <button
                      onClick={() => saveEmailList(flag)}
                      disabled={savingKey === flag.key}
                      className="px-3 py-2 rounded-lg text-xs font-semibold border border-slate-300 hover:bg-slate-50 disabled:opacity-50 shrink-0"
                      data-testid={`system-flag-save-${flag.key}`}
                    >
                      {savingKey === flag.key ? "Guardando..." : "Guardar"}
                    </button>
                  </div>
                ) : flag.type === "bool" ? (
                  <button
                    onClick={() => toggleBool(flag)}
                    disabled={savingKey === flag.key}
                    data-testid={`system-flag-toggle-${flag.key}`}
                    className="flex items-center gap-2 shrink-0 disabled:opacity-50"
                  >
                    {savingKey === flag.key ? (
                      <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
                    ) : flag.value ? (
                      <ToggleRight className="w-9 h-9 text-emerald-600" />
                    ) : (
                      <ToggleLeft className="w-9 h-9 text-slate-300" />
                    )}
                    <span className={`text-xs font-semibold ${flag.value ? "text-emerald-700" : "text-slate-400"}`}>
                      {flag.value ? "Activado" : "Desactivado"}
                    </span>
                  </button>
                ) : (
                  <div className="flex items-center gap-2 shrink-0">
                    <input
                      type="number"
                      min={flag.min ?? 0}
                      value={drafts[flag.key] ?? flag.value}
                      onChange={e => setDrafts(prev => ({ ...prev, [flag.key]: e.target.value }))}
                      className="w-24 border border-slate-200 rounded-lg px-2 py-1.5 text-sm text-right"
                      data-testid={`system-flag-input-${flag.key}`}
                    />
                    <button
                      onClick={() => saveInt(flag)}
                      disabled={savingKey === flag.key}
                      className="px-3 py-1.5 rounded-lg text-xs font-semibold border border-slate-300 hover:bg-slate-50 disabled:opacity-50"
                      data-testid={`system-flag-save-${flag.key}`}
                    >
                      {savingKey === flag.key ? "Guardando..." : "Guardar"}
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
}
